import { ActionItem, ItemDefinition } from "./Tree";
import { ClosedFolderSVG, DragHandleSVG, FileSVG, OpenFolderSVG } from "../../assets/icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDraggable, useDroppable } from "@dnd-kit/core";

import { CssClassNameBuilder } from "../../utils/CssClassNameBuilder1";
import { IconButton } from "../buttons/IconButton";
import { MoreActions } from "./MoreActions";

export type DropPosition = 'before' | 'after' | 'inside';

interface TreeItemProps<T> {
    item: T;
    selectedItem?: T;
    onSelectedItemChanged: (item: T) => void;
    itemDefinition: ItemDefinition<T>;
    isItemActive: (item: T) => boolean;
    onDropPositionChange: (position: DropPosition) => void;
    activeItem?: T | null;
    defaultExpanded?: boolean;
}

function resolveDropPosition(relY: number): DropPosition {
    if (relY < 0.3) return 'before';
    if (relY > 0.7) return 'after';
    return 'inside';
}

export function TreeItem<T>(props: TreeItemProps<T>) {
    const { onDropPositionChange } = props;

    const [expanded, setExpanded] = useState(props.defaultExpanded ?? false);
    const [dropPosition, setDropPosition] = useState<DropPosition>('inside');


    /****************************
    * DATA MANIPULATION EFFECTS
    *****************************/

    const itemId = useMemo(() =>
        props.itemDefinition.getId(props.item),
    [props.item, props.itemDefinition]);

    const children = useMemo(() => {
        return props.itemDefinition.children && props.itemDefinition.children(props.item)
    }, [props.itemDefinition, props.item])

    const otherActions = useMemo(() => {
        if (props.itemDefinition.actions === undefined) return [];
        const actions = props.itemDefinition.actions(props.item);
        if (actions.length <= 1) return [];

        return actions.slice(1);
    }, [props.itemDefinition.actions, props.item])

    const isSelected = useMemo(() => { return props.item === props.selectedItem; }, [props.item, props.selectedItem])

    const contentRef = useRef<HTMLDivElement | null>(null);

    // a tree that does nothing on drop offers no drag handle
    const canDrag = !!(props.itemDefinition.onItemMoved || props.itemDefinition.onItemReordered);

    const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
        id: `drag-${itemId}`,
        data: { item: props.item },
        disabled: !canDrag
    });

    const { setNodeRef: setDropRef, isOver } = useDroppable({
        id: `drop-${itemId}`,
        data: { item: props.item }
    });

    useEffect(() => {
        if (!isOver || isDragging || !contentRef.current) return;
        const rect = contentRef.current.getBoundingClientRect();
        const handler = (e: PointerEvent) => {
            const relY = (e.clientY - rect.top) / rect.height;
            let position = resolveDropPosition(relY);
            if (position === 'inside' && props.activeItem && props.itemDefinition.canItemMoveTo && !props.itemDefinition.canItemMoveTo(props.activeItem, props.item)) {
                position = relY <= 0.5 ? 'before' : 'after';
            }
            setDropPosition(position);
            onDropPositionChange(position);
        };
        window.addEventListener('pointermove', handler);
        return () => window.removeEventListener('pointermove', handler);
    }, [isOver, isDragging, onDropPositionChange, props.activeItem, props.itemDefinition, props.item]);

    function IsActiveDeepSearch(source: T): boolean {
        if (props.isItemActive(source)) {
            return true;
        }

        const children = props.itemDefinition.children && props.itemDefinition.children(source);
        const results = children?.map((c: any) => IsActiveDeepSearch(c));
        const hasChildActivated = results && results.filter(t => t).length > 0;

        return hasChildActivated || false;
    }

    useEffect(() => {
        if (props.isItemActive(props.item)) {
            props.onSelectedItemChanged(props.item);
        } else if (IsActiveDeepSearch(props.item)) {
            setExpanded(true);
        }
    }, [props.isItemActive]);


    /****************************
    * USER INTERACTION
    *****************************/

    const handleExpandClicked = useCallback((event?: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        event?.stopPropagation();
        setExpanded(!expanded)
    }, [expanded, setExpanded])

    const handleItemClicked = useCallback((event?: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        event?.stopPropagation();

        handleExpandClicked(event);

        props.onSelectedItemChanged(props.item)
        props.itemDefinition.onItemClicked && props.itemDefinition.onItemClicked(props.item)
    }, [props.itemDefinition.onItemClicked, props.item, handleExpandClicked])

    const handleMainActionClicked = useCallback((event: React.MouseEvent<HTMLAnchorElement, MouseEvent> | undefined, action: ActionItem<T>) => {
        event?.stopPropagation();
        action.onClick(props.item)
    }, [props.item])



    /****************************
    * CSS && HTML
    *****************************/

    const combinedRef = useCallback((node: HTMLDivElement | null) => {
        contentRef.current = node;
        setDragRef(node);
        setDropRef(node);
    }, [setDragRef, setDropRef]);

    const treeItemContentCss = useMemo(() => {
        const isDropActive = isOver && !isDragging;
        return CssClassNameBuilder.new()
            .add("tree-item-content")
            .addConditional(isSelected, "highlighted")
            .addConditional(isDropActive && dropPosition === 'inside', "drop-target")
            .addConditional(isDropActive && dropPosition === 'before', "drop-before")
            .addConditional(isDropActive && dropPosition === 'after', "drop-after")
            .build();
    }, [isSelected, isOver, isDragging, dropPosition])

    const itemMainActionCss = useMemo(() => {
        return CssClassNameBuilder.new()
            .add("item-main-action")
            .addConditional(isSelected, "highlighted")
            .build();
    }, [isSelected])

    const mainAction = useMemo(() => {
        const [action] = props.itemDefinition.actions && props.itemDefinition.actions(props.item) || [];

        if (action === undefined) {
            return <div className={itemMainActionCss}></div>
        }

        return (
            <IconButton icon={action.icon} className={itemMainActionCss} onClick={(e) => handleMainActionClicked(e, action)} />
        )
    }, [props.itemDefinition.actions, props.item, itemMainActionCss])


    return (
        <div className={`tree-item${isDragging ? ' dragging' : ''}`}>
            <div
                className={treeItemContentCss}
                ref={combinedRef}
                onClick={(e) => handleItemClicked(e)}
            >
                {canDrag ?
                    <div
                        className='tree-drag-handle'
                        {...listeners}
                        {...attributes}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <DragHandleSVG />
                    </div>
                : null}
                {children && children.length > 0 ?
                    <div className='tree-icon tree-expand' onClick={(e) => handleExpandClicked(e)}>{expanded ? <OpenFolderSVG /> : <ClosedFolderSVG />}</div>
                    :
                    <div className='tree-icon'><FileSVG /> </div>
                }
                {props.itemDefinition.icon &&
                    (<div className='tree-icon'>{props.itemDefinition.icon(props.item)}</div>)
                }
                <div className='tree-label'>{props.itemDefinition.render(props.item)}</div>
                <div className='tree-actions'>
                    {mainAction}
                    <MoreActions actions={otherActions} item={props.item} highlighted={isSelected} />
                </div>
            </div>
            {children && expanded ?
                <div className='tree-item-children'>
                    {children.map((child) => (<TreeItem
                        key={props.itemDefinition.getId(child)}
                        item={child}
                        isItemActive={props.isItemActive}
                        itemDefinition={props.itemDefinition}
                        onSelectedItemChanged={props.onSelectedItemChanged}
                        selectedItem={props.selectedItem}
                        onDropPositionChange={onDropPositionChange}
                        activeItem={props.activeItem}
                        defaultExpanded={props.defaultExpanded}
                    />))}
                </div>
                : null}
        </div>
    )
}
